import { Routes, Route, useLocation } from "react-router-dom";
import { Suspense, useEffect, lazy } from "react";
import { useAuth } from "./context/AuthContext";
import { Skeleton } from "./components/ui/skeleton";
import { ImpersonationBar } from "./components/admin/ImpersonationBar";
import { DashboardSidebar } from "./components/layout/sidebar/DashboardSidebar";
import Header from "./components/layout/Header";
import ProtectedRoute from "./components/layout/navigation/ProtectedRoute";
import AIChat from "./components/ai-chat/AIChat";
import TrialExtensionBanner from "./components/subscription/TrialExtensionBanner";
import LoadingSpinner from "./components/ui/LoadingSpinner";

// Import Dashboard directly to avoid dynamic import issues
import Dashboard from "./pages/Dashboard";

// Lazy load other pages with error boundaries for better error handling
const Index = lazy(() => 
  import("./pages/Index").catch(error => {
    console.error("Failed to load Index page:", error);
    return { default: () => <div>Failed to load home page. Please refresh.</div> };
  })
);

const Login = lazy(() => 
  import("./pages/Login").catch(error => {
    console.error("Failed to load Login page:", error);
    return { default: () => <div>Failed to load login page. Please refresh.</div> };
  })
);

// Similarly define other lazy-loaded components with error handling
const Register = lazy(() => import("./pages/Register").catch(e => ({ default: () => <div>Failed to load register page</div> })));
const Forms = lazy(() => import("./pages/Forms").catch(e => ({ default: () => <div>Failed to load forms page</div> })));
const FormPreview = lazy(() => import("./pages/FormPreview").catch(e => ({ default: () => <div>Failed to load form preview</div> })));
const FormView = lazy(() => import("./pages/FormView").catch(e => ({ default: () => <div>Failed to load form view</div> })));
const EmbedOptions = lazy(() => import("./pages/EmbedOptions").catch(e => ({ default: () => <div>Failed to load embed options</div> })));
const Leads = lazy(() => import("./pages/Leads").catch(e => ({ default: () => <div>Failed to load leads page</div> })));
const Settings = lazy(() => import("./pages/Settings").catch(e => ({ default: () => <div>Failed to load settings</div> })));
const NotFound = lazy(() => import("./pages/NotFound").catch(e => ({ default: () => <div>Page not found</div> })));
const Campaigns = lazy(() => import("./pages/Campaigns").catch(e => ({ default: () => <div>Failed to load campaigns</div> })));
const ComplianceVault = lazy(() => import("./pages/ComplianceVault").catch(e => ({ default: () => <div>Failed to load compliance vault</div> })));
const RaiseCapital = lazy(() => import("./pages/RaiseCapital").catch(e => ({ default: () => <div>Failed to load raise capital page</div> })));
const AdminPanel = lazy(() => import("./pages/AdminPanel").catch(e => ({ default: () => <div>Failed to load admin panel</div> })));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy").catch(e => ({ default: () => <div>Failed to load privacy policy</div> })));
const TermsOfService = lazy(() => import("./pages/TermsOfService").catch(e => ({ default: () => <div>Failed to load terms of service</div> })));
const Pricing = lazy(() => import("./pages/Pricing").catch(e => ({ default: () => <div>Failed to load pricing</div> })));
const EarnRewards = lazy(() => import("./pages/EarnRewards").catch(e => ({ default: () => <div>Failed to load earn rewards</div> })));
const TestTheWatersPage = lazy(() => import("./pages/TestTheWaters").catch(e => ({ default: () => <div>Failed to load test the waters page</div> })));
const EquityCrowdfundingCosts = lazy(() => import("./pages/EquityCrowdfundingCosts").catch(e => ({ default: () => <div>Failed to load equity crowdfunding costs</div> })));
const AuthCallback = lazy(() => import("./pages/AuthCallback").catch(e => ({ default: () => <div>Failed to load auth callback</div> })));
const ContactUs = lazy(() => import("./pages/ContactUs").catch(e => ({ default: () => <div>Failed to load contact page</div> })));

function App() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const location = useLocation();
  
  const sidebarPaths = [
    '/dashboard',
    '/forms',
    '/leads',
    '/settings',
    '/campaigns',
    '/compliance-vault',
    '/raise-capital',
    '/admin',
    '/pricing',
    '/earn-rewards'
  ];
  
  const shouldUseSidebar = () => {
    if (!isAuthenticated) return false;
    
    const path = location.pathname;
    return sidebarPaths.some(sidebarPath => 
      path === sidebarPath || path.startsWith(`${sidebarPath}/`)
    );
  };
  
  const shouldShowAIChat = () => {
    const authenticatedPaths = [
      '/dashboard',
      '/forms',
      '/leads',
      '/settings',
      '/campaigns',
      '/raise-capital',
      '/admin',
      '/earn-rewards'
    ];
    
    return isAuthenticated && authenticatedPaths.some(path => 
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  const shouldShowTrialBanner = () => {
    if (!isAuthenticated) return false;
    
    const marketingPages = ['/', '/login', '/register', '/privacy', '/terms', '/contact-us', 
                          '/test-the-waters', '/equity-crowdfunding-costs'];
    
    return !marketingPages.includes(location.pathname);
  };
  
  useEffect(() => {
    if (isAuthenticated && location.pathname !== '/') {
      const existingButtons = document.querySelectorAll('.rs-floating-button');
      const existingModals = document.querySelectorAll('.rs-modal');
      
      existingButtons.forEach(button => button.remove());
      existingModals.forEach(modal => modal.remove());
      
      const floatingButtonScript = document.getElementById('investor-form-button');
      if (floatingButtonScript) {
        floatingButtonScript.remove();
      }
    }
  }, [location.pathname, isAuthenticated]);
  
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="space-y-4 w-full max-w-md">
          <Skeleton className="h-8 w-3/4 mx-auto" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-8 w-1/2 mx-auto" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="App">
      <ImpersonationBar />
      {shouldUseSidebar() ? (
        <DashboardSidebar>
          <RenderRoutes />
        </DashboardSidebar>
      ) : (
        <>
          <Header />
          <RenderRoutes />
        </>
      )}
      {shouldShowAIChat() && <AIChat />}
      {shouldShowTrialBanner() && <TrialExtensionBanner />}
    </div>
  );
}

function RenderRoutes() {
  const location = useLocation();
  
  // Enhanced fallback with better error handling
  const loadingFallback = (
    <div className="flex flex-col justify-center items-center min-h-[60vh] p-8">
      <LoadingSpinner size="sm" />
      <div className="mt-4 text-sm text-muted-foreground">Loading page...</div>
    </div>
  );
  
  return (
    <Routes location={location} key={location.pathname}>
      {/* Home route with enhanced error handling */}
      <Route path="/" element={
        <Suspense fallback={loadingFallback}>
          <Index />
        </Suspense>
      } />
      <Route path="/login" element={
        <Suspense fallback={loadingFallback}>
          <Login />
        </Suspense>
      } />
      <Route path="/register" element={
        <Suspense fallback={loadingFallback}>
          <Register />
        </Suspense>
      } />
      <Route path="/auth/callback" element={
        <Suspense fallback={loadingFallback}>
          <AuthCallback />
        </Suspense>
      } />
      <Route path="/pricing" element={
        <Suspense fallback={loadingFallback}>
          <Pricing />
        </Suspense>
      } />
      <Route path="/test-the-waters" element={
        <Suspense fallback={loadingFallback}>
          <TestTheWatersPage />
        </Suspense>
      } />
      <Route path="/equity-crowdfunding-costs" element={
        <Suspense fallback={loadingFallback}>
          <EquityCrowdfundingCosts />
        </Suspense>
      } />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/forms" element={
        <Suspense fallback={loadingFallback}>
          <ProtectedRoute requiresSubscription={true}>
            <Forms />
          </ProtectedRoute>
        </Suspense>
      } />
      <Route path="/forms/:formId" element={
        <Suspense fallback={loadingFallback}>
          <ProtectedRoute requiresSubscription={true}>
            <Forms />
          </ProtectedRoute>
        </Suspense>
      } />
      <Route path="/form-preview/:formId" element={
        <Suspense fallback={loadingFallback}>
          <FormPreview />
        </Suspense>
      } />
      <Route path="/form-view/:formId" element={
        <Suspense fallback={loadingFallback}>
          <FormView />
        </Suspense>
      } />
      <Route path="/leads" element={
        <Suspense fallback={loadingFallback}>
          <Leads />
        </Suspense>
      } />
      <Route path="/settings" element={
        <Suspense fallback={loadingFallback}>
          <Settings />
        </Suspense>
      } />
      <Route path="/campaigns" element={
        <Suspense fallback={loadingFallback}>
          <Campaigns />
        </Suspense>
      } />
      <Route path="/compliance-vault" element={
        <Suspense fallback={loadingFallback}>
          <ComplianceVault />
        </Suspense>
      } />
      <Route path="/raise-capital" element={
        <Suspense fallback={loadingFallback}>
          <RaiseCapital />
        </Suspense>
      } />
      <Route path="/admin" element={
        <Suspense fallback={loadingFallback}>
          <AdminPanel />
        </Suspense>
      } />
      <Route path="/earn-rewards" element={
        <Suspense fallback={loadingFallback}>
          <EarnRewards />
        </Suspense>
      } />
      <Route path="/privacy" element={
        <Suspense fallback={loadingFallback}>
          <PrivacyPolicy />
        </Suspense>
      } />
      <Route path="/terms" element={
        <Suspense fallback={loadingFallback}>
          <TermsOfService />
        </Suspense>
      } />
      <Route path="/contact-us" element={
        <Suspense fallback={loadingFallback}>
          <ContactUs />
        </Suspense>
      } />
      <Route path="/embed-options/:formId" element={
        <Suspense fallback={loadingFallback}>
          <EmbedOptions />
        </Suspense>
      } />
      <Route path="*" element={
        <Suspense fallback={loadingFallback}>
          <NotFound />
        </Suspense>
      } />
    </Routes>
  );
}

export default App;
